import { Effect } from 'postprocessing';
import { Uniform, Vector2, Vector3 } from 'three';
import type { Camera, WebGLRenderer } from 'three';

// A ring that bends the picture outwards as it sweeps across the screen from the blow. Measured on the screen, so it
// looks the same however near the camera the blow is.
const fragment = /* glsl */ `
uniform vec2 centre;
uniform float radius;
uniform float thickness;
uniform float strength;

void mainUv(inout vec2 uv) {
	if (strength <= 0.0) return;
	vec2 fromCentre = (uv - centre) * vec2(aspect, 1.0);
	float distanceOut = length(fromCentre);
	float ring = 1.0 - smoothstep(0.0, thickness, abs(distanceOut - radius));
	uv -= fromCentre / max(distanceOut, 1e-4) / vec2(aspect, 1.0) * ring * strength;
}`;

const seconds = 0.4;
const reach = 0.55;
const onScreen = new Vector3();

/** The shock wave of a heavy blow: a ring of bent air racing out from where it landed. */
export class ShockRing extends Effect {
  private age = seconds;
  private power = 0;
  private readonly at = new Vector3();

  constructor(private readonly camera: Camera) {
    super('ShockRing', fragment, {
      uniforms: new Map<string, Uniform>([
        ['centre', new Uniform(new Vector2(0.5, 0.5))],
        ['radius', new Uniform(0)],
        ['thickness', new Uniform(0.07)],
        ['strength', new Uniform(0)],
      ]),
    });
  }

  /** Whether a ring is sweeping the screen; at rest it leaves the picture as it is. */
  get active(): boolean {
    return this.age < seconds;
  }

  /** A ring from this point in the world, strength 0..1. */
  explode(at: Vector3, strength: number): void {
    this.at.copy(at);
    this.power = strength;
    this.age = 0;
  }

  override update(_renderer: WebGLRenderer, _input: unknown, delta = 0): void {
    this.age += delta;
    const t = Math.min(1, this.age / seconds);
    const strength = this.uniforms.get('strength')!;
    if (t >= 1) {
      strength.value = 0;
      return;
    }
    // Where the blow is on the screen now: the camera may be moving.
    onScreen.copy(this.at).project(this.camera);
    (this.uniforms.get('centre')!.value as Vector2).set((onScreen.x + 1) / 2, (onScreen.y + 1) / 2);
    this.uniforms.get('radius')!.value = reach * Math.sqrt(t);
    strength.value = 0.03 * this.power * (1 - t) * (1 - t);
  }
}
